import { PasswordInput } from '@components/App/PasswordInput'
import Recaptcha from '@components/App/RecaptchaWidget/index'
import { AuthActions } from '@store/reducers/auth'
import type { AxiosError } from 'axios'
import type React from 'react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import API from '@/api'
import { PASSWORD_REGEX } from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useForm } from '@/hooks'
import ContinueWithSocial from './ContinueWithSocial'
import css from './Register.module.scss'
import { RegisterCommit } from './RegisterCommit'

type Props = {
  redirectAfterAuth?: string
}

// @ts-expect-error
// biome-ignore lint/suspicious/noShadowRestrictedNames: Error is a domain component name
const Error: React.FC = ({ children }) => (
  <div className={css.error}>{children}</div>
)

export const RegisterForm: React.FC<Props> = ({ redirectAfterAuth }) => {
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [state, setState] = useState({
    isLoading: false,
    errors: {
      emailAddress: '',
    },
  })

  const { form, errors, register: registerField, onSubmit } = useForm()
  const dispatch = useDispatch()

  const handleSubmit = async () => {
    if (!PASSWORD_REGEX.exec(password)) {
      setPasswordError('Please follow the password rules')

      return null
    }

    if (state.isLoading) {
      return null
    }

    setState((prevState) => ({ ...prevState, isLoading: true }))

    const utm = window.localStorage.getItem('utm') || ''

    try {
      const coupon = form.coupon?.length === 0 ? null : form.coupon
      const payload = await API.Auth.register({
        username: form.emailAddress,
        password,
        coupon,
        utm_source: utm,
      })

      window.localStorage.removeItem('utm')

      // Account created (email not yet verified) — count the signup here, at the
      // register success, not on the later profile-setup step.
      sendGtmEvent(GtmTriggerName.AUTH_SIGN_UP, 'Registered a new account')

      if (redirectAfterAuth) {
        navigate(
          `?${new URLSearchParams({
            redirectAfterAuth: encodeURIComponent(redirectAfterAuth),
          })}`,
          { replace: true }
        )
      }

      dispatch(AuthActions.setToken(payload.token))
    } catch (err) {
      const data = JSON.parse((err as AxiosError).message)
      setState({ isLoading: false, errors: data?.data?.errors })
      sendGtmEvent(GtmTriggerName.AUTH_SIGN_UP_ERROR, data?.message)
    }
  }

  const onPasswordChange = (value: string) => {
    setPassword(value)

    setPasswordError(null)
  }

  return (
    <Recaptcha>
      <form
        onSubmit={(e: React.ChangeEvent<HTMLFormElement>) => {
          if (password.length === 0) {
            setPasswordError('This field is required')
          } else if (!PASSWORD_REGEX.exec(password)) {
            setPasswordError('Please follow the password rules')
          }

          onSubmit(e, handleSubmit)
        }}
      >
        <div className={css.inputWrapper}>
          <input
            className={css.input}
            name="emailAddress"
            type="text"
            placeholder="Email"
            {...registerField(
              'emailAddress',
              '',
              {
                required: true,
                email: true,
              },
              () => {
                setState((prevState) => ({
                  ...prevState,
                  errors: {
                    ...prevState.errors,
                    emailAddress: '',
                  },
                }))
              }
            )}
          />

          {state.errors.emailAddress && (
            // @ts-expect-error
            <Error>{state.errors.emailAddress}</Error>
          )}

          {/*// @ts-ignore*/}
          {errors.emailAddress && <Error>{errors.emailAddress}</Error>}
        </div>

        <div className={css.inputWrapper}>
          <PasswordInput
            placeholder="Password"
            name="password"
            id="password"
            onChange={onPasswordChange}
            error={passwordError}
          />
        </div>

        <RegisterCommit
          isLoading={state.isLoading}
          errors={{
            ...state.errors,
            ...errors,
          }}
        />

        <ContinueWithSocial
          disabled={state.isLoading}
          redirectAfterAuth={redirectAfterAuth}
        />
      </form>
    </Recaptcha>
  )
}
